/**
 * Hook for fetching and managing user achievements
 *
 * Handles fetching badges, progress toward next badge, and surprise badges.
 * Note: Toast notifications for earned badges are handled by engagementTracker.js
 * at the moment of earning, not when viewing the profile.
 */
import { useState, useEffect, useCallback } from 'react';
import Util from '../util/util';
import {
  checkNewlyEarnedBadges,
  getInitialBadgeCount,
} from '../util/surpriseBadgeConfig';

/**
 * Badge configuration matching backend definitions
 * Used for displaying locked badges before they're earned
 */
export const BADGES = {
  first_signal: {
    id: 'first_signal',
    name: 'First Signal',
    subtitle: 'Attentiveness Recognized',
    threshold: 1,
    color: 'gold',
  },
  consistent_observer: {
    id: 'consistent_observer',
    name: 'Consistent Observer',
    subtitle: 'Staying Alert',
    threshold: 5,
    color: 'blue',
  },
  safety_contributor: {
    id: 'safety_contributor',
    name: 'Safety Contributor',
    subtitle: 'Making a Difference',
    threshold: 10,
    color: 'green',
  },
  dedicated_advocate: {
    id: 'dedicated_advocate',
    name: 'Dedicated Advocate',
    subtitle: 'Committed to Safety',
    threshold: 15,
    color: 'teal',
  },
  culture_builder: {
    id: 'culture_builder',
    name: 'Culture Builder',
    subtitle: 'Shaping Safety',
    threshold: 25,
    color: 'purple',
  },
};

/**
 * Get all badges sorted by threshold
 */
export const getAllBadges = () => {
  return Object.values(BADGES).sort((a, b) => a.threshold - b.threshold);
};

/**
 * Main hook for managing user achievements
 */
export default function useAchievements() {
  const [achievements, setAchievements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [surpriseBadgeCount, setSurpriseBadgeCount] = useState(0);

  /**
   * Fetch achievements from API
   */
  const fetchAchievements = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await Util.get('Resty', '/items/achievements');
      setAchievements(data);
      setSurpriseBadgeCount(data.surpriseBadgeCount || 0);
    } catch (err) {
      console.error('Failed to fetch achievements:', err);
      setError(err);
      // Return default state on error
      setAchievements({
        badges: [],
        totalReports: 0,
        nextBadge: { name: 'First Signal', reportsUntilEarned: 1 },
        streak: { current: 0, longest: 0 },
        surpriseBadgeCount: 0,
        engagementCounts: {},
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchAchievements();
  }, [fetchAchievements]);

  /**
   * Get badges with locked state for display
   * Returns all badges with earned status
   */
  const getBadgesWithStatus = useCallback(() => {
    const allBadges = getAllBadges();
    const earnedIds = new Set(achievements?.badges?.map((b) => b.id) || []);

    return allBadges.map((badge) => {
      const earnedBadge = achievements?.badges?.find((b) => b.id === badge.id);
      return {
        ...badge,
        earned: earnedIds.has(badge.id),
        earnedAt: earnedBadge?.earnedAt || null,
      };
    });
  }, [achievements]);

  /**
   * Check what surprise badges would be earned given engagement counts
   * Useful for previewing before API call
   */
  const checkPendingSurpriseBadges = useCallback((engagementCounts) => {
    const earnedIds = achievements?.earnedSurpriseBadgeIds || [];
    return checkNewlyEarnedBadges(engagementCounts, earnedIds);
  }, [achievements]);

  return {
    achievements,
    loading,
    error,
    refetch: fetchAchievements,
    getBadgesWithStatus,
    // Convenience getters for report-based badges
    badges: achievements?.badges || [],
    totalReports: achievements?.totalReports || 0,
    nextBadge: achievements?.nextBadge || null,
    streak: achievements?.streak || { current: 0, longest: 0 },
    // Surprise badge getters (ongoing - no fixed total)
    surpriseBadgeCount,
    initialBadgeCount: getInitialBadgeCount(), // Count of defined triggers before ongoing kicks in
    checkPendingSurpriseBadges,
    engagementCounts: achievements?.engagementCounts || {},
  };
}
